import SearchInput from "~/common/components/SearchInput";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import classes from "~/pages/ContractOverviewPage/ContractOverviewPage.module.less";
import { useEffect, useState } from "react";
import NewContractTable from "~/features/contracts/components/ContractOverview/NewContractTable";
import { fetchContractsOverview } from "~/features/contracts/api/getContractsOverview";
import type { UpdatedContractOverviewData } from "~/features/contracts/redux/types";
import EmptyContractsScreen from "~/features/contracts/components/ContractOverview/EmptyContractsScreen";
import Spinner from "~/common/components/Spinner/Spinner";
import { Link } from "~/common/components/Link";
import { NEW_CONTRACT_PAGE } from "~/utils/url/staticUrls";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useTranslation } from "react-i18next";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";

export const ContractOverviewPage = () => {
  const { t } = useTranslation();
  const { val: snackBar, setTrue: showSnackbar, setFalse: hideSnackbar } = useBooleanState(false);
  const [contractState, setContractState] = useState<UpdatedContractOverviewData[]>();
  const [searchQuery, setSearchQuery] = useState("");
  const [pageState, setPageState] = useState<"loading" | "loaded" | "empty" | "error">("loading");

  const filteredContracts = contractState?.filter((x) => {
    return Object.values({ ...x, price: x.netPrice.value, isExpanded: undefined })
      .join(" ")
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    fetchContractsOverview().then((res) => {
      if (!res) {
        return;
      }
      if (res.length === 0) {
        setPageState("empty");
      } else {
        setContractState(res);
        setPageState("loaded");
      }
    });
    if (searchParams.has("highlight")) {
      const query = searchParams.get("highlight");
      if (query) {
        setSearchQuery(query);
      }
    }
    if (searchParams.has("success")) {
      showSnackbar();
    }
  }, []);

  if (pageState === "loading") {
    return <Spinner />;
  }

  const closeSnackbar = () => {
    const searchParams = new URLSearchParams(window.location.search);
    hideSnackbar();
    searchParams.delete("success");
    window.history.replaceState(null, "", `?${searchParams.toString()}`);
  };

  return (
    <div>
      <Snackbar
        open={snackBar}
        onClose={closeSnackbar}
        message={t("contractOverview.snackbar.info")}
        action={<SnackbarCloseButton onClick={closeSnackbar} />}
      />
      <PageTitle>
        <div className={classes.titleHeader}>
          <div>{t("contractsList.header", { amount: contractState?.length ?? 0 })}</div>

          {pageState === "loaded" && (
            <div>
              <Link
                href={NEW_CONTRACT_PAGE}
                className={classes.requestButton}
                iconBefore={<Icon type={IconType.Plus} />}
                clta-main-page-element-marker="request-new-contract-link"
              >
                {t("newContract.title")}
              </Link>
            </div>
          )}
        </div>
      </PageTitle>
      {filteredContracts && pageState === "loaded" && (
        <div className={classes.mainContainer}>
          <div className={classes.searchContainer}>
            <SearchInput
              helperLabel={t("contractsList.searchHelperLabel")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e)}
              label={t("contractsList.searchLabel")}
            />
          </div>
          {<NewContractTable contractData={filteredContracts} />}
        </div>
      )}
      {pageState === "empty" && <EmptyContractsScreen />}
    </div>
  );
};
